import { SearchArticles } from '@/types';

export type Tag = {
    id: number;
    title: string;
};

export type TagsResponse = Tag[];

export type ActivityItem = {
    originId: number | null;
    title: string;
    image: string | null;
    type: number;
    sortIndex: number;
    isMultiple: boolean;
    rightAnswer: number[];
    textOptions: {
        originId: number;
        text: string;
        sourceOptionId?: number;
    }[];
    article: {
        videoData: {
            articleId: number;
            images: {
                large: string;
                short?: string;
            };
            title: string;
            videoHash: string | null;
            videoUrl: string | null;
            subtitles?: Record<string, string>;
        };
    } | null;
    isSelect: boolean;
    questionOrder?: number;
};

export type Activity = {
    originId: number | null;
    title: string;
    sortIndex: number;
    isSelect: boolean;
    items: ActivityItem[];
};

export type QuizFormData = {
    article?: number | null;
    /** jove_section.id from article_list row (VIDEO library create → joveSectionId). */
    articleSectionId?: number | null;
    /** Display title from Select Article picker; used to skip a redundant getArticle fetch. */
    articleTitle?: string | null;
    title: string;
    isGraded: string;
    tags: number[];
    startDate: Date | null;
    endDate: Date | null;
    duration: number;
    activities: Activity[];
    showCaption?: boolean;
    restrictNonInstitutional?: boolean;
    /** Library quiz V2 (video/chapter/textbook) Draft/Active status. Defaults to 'ACTIVE'. */
    status?: 'ACTIVE' | 'DRAFT';
};

export type Quiz = {
    id: number;
    article?: number | null;
    title: string;
    active: boolean;
    startDate: string | null;
    endDate: string | null;
    createdAt: string | null;
    createdBy?: {
        email: string;
        firstName: string;
        id: number;
        lastName: string;
    };
    tags?:
        | {
              id: number;
              image: string | null;
              nonRemovable: boolean;
              title: string;
              type: number;
          }[]
        | null;
    typeBySource?: number;
    inviteToken?: string;
    duration: number | null;
    showCaption?: boolean;
    countOfQuestions: number;
    restrictNonInstitutional?: boolean;
    isAssignSomeone?: boolean;
    isGraded?: boolean;
    editable: boolean;
    activities?: (Omit<Activity, 'items' | 'originId' | 'isSelect'> & {
        id: number | null;
        items?: (Omit<ActivityItem, 'textOptions' | 'originId' | 'article' | 'isSelect'> & {
            id: number | null;
            article: {
                videoData: {
                    articleId: number;
                    images: {
                        large: string;
                        short?: string;
                    };
                    title: string;
                    videoHash: string | null;
                    videoUrl: string | null;
                };
            } | null;
            textOptions: {
                id: number;
                text: string;
            }[];
        })[];
    })[];
    quizType?: 'adminQuiz' | 'userQuiz';
    /** Quiz V2 (quiz-svc) UUID for admin flows that use UUID routes */
    quizUuid?: string;
};

export type User = {
    id: number;
    email: string | null;
    firstName: string;
    lastName: string;
};

export type SharedList = {
    sharedBy: User;
    user: User;
};

export type ShareResponse = {
    already_shared: string[];
    not_access: string[];
    not_found: string[];
    shared: string[];
};

export type SharedListAdminQuiz = {
    user: {
        id: number;
        email: string;
        firstName: string;
        lastName: string;
    };
};

export type SharedListMyQuiz = {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
};

export type AssignType = {
    createdAt: string | null;
    id: number;
    submitAt: string | null;
    updatedAt: string | null;
    startAt: string | null;
    progress?: number | null;
    endDate?: number | null;
    user: User;
};

export type QuizzesResponse = {
    navigation: {
        items_on_page: number;
        items_per_page: number;
        more: boolean;
        page: number;
        total_items: number;
        total_pages: number;
    };
    result: Quiz[];
};

export type CreateQuizData = Omit<
    Quiz,
    'id' | 'active' | 'createdAt' | 'startDate' | 'endDate' | 'isGraded' | 'duration' | 'countOfQuestions' | 'editable' | 'activities' | 'tags'
> & {
    tags: number[];
    activities: (Omit<Activity, 'items' | 'originId' | 'isSelect'> & {
        items: (Omit<ActivityItem, 'textOptions' | 'originId' | 'isSelect' | 'article'> & {
            article: number | null;
            textOptions: {
                id: number;
                text: string;
            }[];
        })[];
    })[];
};

export type UpdateQuizData = Omit<
    Quiz,
    'id' | 'active' | 'createdAt' | 'startDate' | 'endDate' | 'isGraded' | 'duration' | 'countOfQuestions' | 'editable' | 'activities' | 'tags'
> & {
    tags: number[];
    activities: (Omit<Activity, 'items' | 'originId' | 'isSelect'> & {
        id: number | null;
        items: (Omit<ActivityItem, 'textOptions' | 'originId' | 'isSelect' | 'article' | 'rightAnswer'> & {
            id: number | null;
            article: number | null;
            textOptions?: {
                id: number;
                text: string;
            }[];
            rightAnswer?: number[];
        })[];
    })[];
};

export type CreateQuizLibraryData = Omit<
    Quiz,
    'id' | 'article' | 'active' | 'createdAt' | 'countOfQuestions' | 'editable' | 'activities' | 'tags'
> & {
    tags: number[];
    activities: (Omit<Activity, 'items' | 'originId' | 'isSelect'> & {
        items: (Omit<ActivityItem, 'textOptions' | 'originId' | 'isSelect' | 'article'> & {
            article: number | null;
            textOptions: {
                id: number;
                text: string;
            }[];
        })[];
    })[];
};

export type UpdateQuizLibraryData = Omit<
    Quiz,
    'id' | 'article' | 'active' | 'createdAt' | 'countOfQuestions' | 'editable' | 'activities' | 'tags'
> & {
    tags: number[];
    activities: (Omit<Activity, 'items' | 'originId' | 'isSelect'> & {
        id: number | null;
        items: (Omit<ActivityItem, 'textOptions' | 'originId' | 'isSelect' | 'article' | 'rightAnswer'> & {
            id: number | null;
            article: number | null;
            textOptions?: {
                id: number;
                text: string;
            }[];
            rightAnswer?: number[];
        })[];
    })[];
};

export type QuizLibrary = {
    quizType?: 'adminQuiz' | 'userQuiz';
    sharedUserList?: SharedListMyQuiz[];
    assignList?: string[];
    countOfVideo?: number;
    createdBy?: {
        email: string;
        firstName: string;
        id: number;
        lastName: string;
    };
    inviteToken?: string;
    restrictNonInstitutional?: boolean;
    showCaption?: boolean;
    typeBySource?: number;
    /** Quiz V2 UUID — when set, share uses POST /api/v2/admin/quiz/{quizUuid}/share */
    quizUuid?: string;
    id: number;
    title: string;
    active: boolean;
    processStatus?: number;
    editable: boolean;
    countOfQuestions: number;
    duration: number;
    isGraded: boolean;
    tags:
        | {
              id: number;
              image: string | null;
              nonRemovable: boolean;
              title: string;
              type: number;
          }[]
        | null;
    createdAt: string | null;
    updateAt?: string | null;
    startDate: string | null;
    endDate: string | null;
    activities?: (Omit<Activity, 'items' | 'originId' | 'isSelect'> & {
        id: number | null;
        items?: (Omit<ActivityItem, 'textOptions' | 'originId' | 'article' | 'isSelect'> & {
            id: number | null;
            article: {
                videoData: {
                    articleId: number;
                    images: {
                        large: string;
                        short?: string;
                    };
                    title: string;
                    videoHash: string | null;
                    videoUrl: string | null;
                };
            } | null;
            textOptions: {
                id: number;
                text: string;
            }[];
        })[];
    })[];
    sharedList?: SharedListAdminQuiz[];
};

export type QuizSharedByMe = {
    active: boolean;
    countOfQuestions: number;
    countOfVideo: number;
    endDate: string | null;
    createdAt: string | null;
    duration?: number | null;
    editable: boolean;
    id: number;
    isGraded: boolean;
    startDate: string;
    title: string;
    typeBySource: number;
    assignList?: AssignType[];
    sharedList?: SharedList[];
};

export type QuizSharedByMeType = {
    adminQuiz: QuizSharedByMe | null;
    createdAt: string | null;
    id: number;
    userQuiz: QuizSharedByMe | null;
};

export type QuizSharedByMeResponse = {
    navigation: {
        items_on_page: number;
        items_per_page: number;
        more: boolean;
        page: number;
        total_items: number;
        total_pages: number;
    };
    result: QuizSharedByMeType[];
};

export type QuizLibraryResponse = {
    navigation: {
        items_on_page: number;
        items_per_page: number;
        total_items: number;
        total_pages: number;
        more: boolean;
        page: number;
    };
    result: QuizLibrary[];
};

/** Quiz V2 admin list row (POST api/v2/admin/quiz/list) */
export type AdminQuizV2ListItem = {
    id: number;
    quizUuid: string;
    title: string;
    noOfQuestions: number;
    startTime: string | null;
    endTime: string | null;
    createdTime: string | null;
    updatedTime: string | null;
};

export type AdminQuizV2ListResponse = {
    page: number;
    size: number;
    total: number;
    data: AdminQuizV2ListItem[];
};

/** Current-user quiz row from POST api/v2/admin/quiz/my-quizzes/search (quiz-svc QuizSummaryDto). */
export type MyQuizV2ListItem = {
    id: number;
    uuid: string;
    title: string;
    type?: string | null;
    status?: string | null;
    startTime?: string | null;
    endTime?: string | null;
    durationInMinutes?: number | null;
    maxAttemptCount?: number | null;
    evaluationType?: string | null;
    accessType?: 'INSTITUTION' | 'OPEN' | string | null;
    noOfQuestions?: number | null;
};

export type MyQuizV2ListResponse = {
    page: number;
    size: number;
    total: number;
    data: MyQuizV2ListItem[];
};

/** Shared-with user payload added by Symfony (api/v2/admin/quiz/shared) */
export type QuizV2SharedUserPayload = {
    userId: number;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
};

/** Customer (instructor) quiz row from POST api/v2/admin/quiz/customerQuizzes/list (+ createdByUser from API). */
export type CustomerQuizV2ListItem = {
    id: number;
    /** Quiz-svc UUID (JSON string). Prefer this; some responses may only send `quizUuid`. */
    uuid?: string;
    /** @deprecated Prefer `uuid` — kept for alternate JSON property names on the wire. */
    quizUuid?: string;
    title: string;
    createdBy?: number | null;
    createdByUser?: QuizV2SharedUserPayload | null;
    noOfQuestions?: number;
    startTime?: string | null;
    endTime?: string | null;
    createdTime?: string | null;
    updatedTime?: string | null;
    restrictNonInstitutional?: boolean;
    isGraded?: boolean;
};

/**
 * Row model for {@link CustomerQuizItemV2} only — not {@link QuizLibrary}.
 * V2 uses `uuid` (not `quizUuid`). No `editable` (that is v1-only).
 */
export type CustomerQuizItemDto = {
    id: number;
    uuid: string;
    title: string;
    restrictNonInstitutional: boolean;
    createdBy?: {
        id: number;
        email: string;
        firstName: string;
        lastName: string;
    };
    createdAt: string | null;
    endDate: string | null;
};

export function toCustomerQuizItemDto(row: CustomerQuizV2ListItem): CustomerQuizItemDto {
    const u = row.createdByUser;
    const uuid = row.uuid ?? row.quizUuid ?? '';
    return {
        id: row.id,
        uuid,
        title: row.title,
        restrictNonInstitutional: row.restrictNonInstitutional ?? false,
        createdBy: u
            ? {
                  id: u.userId,
                  email: u.email ?? '',
                  firstName: u.firstName ?? '',
                  lastName: u.lastName ?? '',
              }
            : undefined,
        createdAt: row.createdAt ?? null,
        endDate: row.endTime ?? null,
    };
}

export type CustomerQuizV2ListResponse = {
    page: number;
    size: number;
    total: number;
    data: CustomerQuizV2ListItem[];
};

/** One attempt row from quiz-svc (list assignments / list attempts). */
export type QuizV2AssignmentAttemptRow = {
    attemptUuid: string;
    startedAt?: string | null;
    submittedAt?: string | null;
    attemptNumber?: number | null;
    status?: string;
    evaluationStatus?: string | null;
    scorePercentage?: string | number | null;
    obtainedScore?: string | number | null;
    maxScore?: string | number | null;
};

/** GET …/customerQuiz/{quizUuid}/assignments — one row after Symfony enrichment. */
export type QuizV2AssignmentSummaryItem = {
    assignmentInfo: {
        assignmentUuid?: string;
        quizUuid?: string;
        quizId?: number;
        assigneeId?: number;
        sourceType?: string;
        sourceIdentifier?: string | null;
        createdAt?: string | null;
    };
    attempts: QuizV2AssignmentAttemptRow[];
    user?: {
        id: number;
        email: string | null;
        firstName?: string | null;
        lastName?: string | null;
    };
    class?: {
        id: number;
        name: string | null;
    };
};

export type CustomerQuizAssignmentsListResponse = {
    page: number;
    size: number;
    total: number;
    data: QuizV2AssignmentSummaryItem[];
};

export type QuizV2QuestionResponseItem = {
    questionId?: number;
    questionType?: string;
    subType?: string | null;
    questionContentSnapshot?: string | null;
    learnerResponseJson?: string | null;
    obtainedScore?: string | number | null;
    correct?: boolean | null;
    correctAnswerOptions?: { optionId?: number; content?: string; sortIndex?: number | null }[];
};

/** GET api/v2/admin/quiz/userQuiz/attempt/{attemptUuid}/gradeSyncLogs */
export type GradeSyncLogItem = {
    timestamp?: string | null;
    status?: string;
    statusCode?: number | null;
    message?: string | null;
    score?: number | null;
    scoreUrl?: string | null;
    isError?: boolean | null;
    errorCode?: number | null;
};

export type AttemptGradeSyncLogsResponse = {
    attemptUuid: string;
    assigneeId?: number | null;
    logs: GradeSyncLogItem[];
    user?: {
        id: number;
        email: string | null;
        firstName?: string | null;
        lastName?: string | null;
    };
};

/** GET api/quiz/v2/instructor/attempts/{attemptUuid} */
export type QuizV2AttemptDetailsResponse = {
    attempt?: QuizV2AssignmentAttemptRow;
    assignment?: QuizV2AssignmentSummaryItem['assignmentInfo'];
    questionResponses?: QuizV2QuestionResponseItem[];
    aggregatedTotals?: {
        totalObtainedScore?: string | number | null;
        scorePercentage?: string | number | null;
    };
    quizEvaluationType?: string;
    user?: {
        id: number;
        email: string | null;
        firstName?: string | null;
        lastName?: string | null;
    };
};

/** One row from POST api/v2/admin/quiz/shared (quiz-svc QuizSharedByAdminDto + enrichment) */
export type QuizSharedByAdminV2Row = {
    quizId: number;
    quizUuid: string;
    quizTitle: string;
    noOfQuestions: number;
    noOfVideos?: number;
    startTime: string | null;
    endTime: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    sharedWith: number;
    sharedBy: number;
    sharedAt: string | null;
    sharedWithUser?: QuizV2SharedUserPayload | null;
    sharedByUser?: QuizV2SharedUserPayload | null;
};

export type QuizSharedByAdminV2ListResponse = {
    page: number;
    size: number;
    total: number;
    data: QuizSharedByAdminV2Row[];
};

export type TextOption = {
    id: number;
    text: string;
};
export type PlayListQuizItem = {
    id: number | null;
    title: string;
    type: number;
    sortIndex: number;
    isMultiple?: boolean | null;
    image?: string | null;
    textOptions: TextOption[];
    rightAnswer: number[];
    articleData: {
        articleId: number;
        images: {
            large: string;
        };
        title: string;
        videoHash: string | null;
        videoUrl: string | null;
    } | null;
};

export type ImportPlaylistResponse = {
    items: PlayListQuizItem[];
};

export type ImportPlaylistQuizItem = Omit<ActivityItem, 'textOptions' | 'originId' | 'article' | 'isSelect'> & {
    id: number | null;
    article: {
        videoData: {
            articleId: number;
            images: {
                large: string;
            };
            title: string;
            videoHash: string | null;
            videoUrl: string | null;
        };
    } | null;
    textOptions: {
        id: number;
        text: string;
    }[];
};

export type SaveQuizError = {
    code?: string;
    message?: string;
    errors?: [string, { message: string }[]];
    content: {
        message: string;
        code: string;
        errors?: Record<
            string,
            | {
                  message: string;
                  code: string;
                  payload: null;
              }[]
            | null
        > | null;
    };
    datetime: string;
    elapsedTimeSec: string;
    statusCode: number;
    statusText: string;
};

export type SearchResponse = {
    navigation: {
        items_on_page: number;
        items_per_page: number;
        total_items: number;
        total_pages: number;
        more: boolean;
        page: number;
    };
    result: SearchArticles[];
};

export type AssignQuizToUsersRequest = {
    emails: string[];
    isAdminQuiz?: boolean;
};
